/* @flow */
// This module exports a function which runs a function after a specified number
// of milliseconds. You might think, "isn't that just setTimeout"? But
// setTimeout has a few gotchas that make it insufficient in situations where
// you care about accurate timing:
//
// * The number of ms to wait is always clamped to 4ms minimum
// * The browser only guarantees that it will call your callback some amount of
//   time after the number of ms you request; you could request that something
//   be run in 1ms and it won't be run until 900ms later. The browser is allowed
//   to throttle setTimeout for whatever reason it wants, eg to preserve battery
//   life.
//
// On Android Chrome in particular, we are seeing setTimeout being throttled
// very heavily, which can create a situation where async device parsing never
// completes in time for the next poll cycle because the delay between each
// device parse is so large. This results in highly delayed device updates and
// memory leaks.
//
// As such, this module will default to using setTimeout to enqueue callbacks,
// but you can use the provided `setImplementation` function to replace the
// implementation with one that has better timing guarantees.
//
// If you replace the implementation for this module, you must uphold this
// contract:
//
// * enqueueIn must not run its received function callbacks synchronously, even
//   if the ms value is 0 (or negative).
// * enqueueIn should not call the passed callback until at least the specified
//   number of ms have passed. enqueueIn MAY call the callback *after* the
//   number of ms have passed- but it should try to call it asap.
// * enqueueIn must return an opaque value which can be passed into
//   cancelEnqueued to abort calling the callback.
// * If an invalid value is passed into cancelEnqueued, cancelEnqueued must
//   not throw an exception.
//
// When you use enqueueIn, you should not assume that any instrinsic behaviors
// of setTimeout aside from those listed in the above contract will be present.
// Most notably, you should not rely on details of the task/microtask queue;
// although functions passed to setTimeout are guaranteed to run after the
// `Promise.resolve().then(...)`, this is not guaranteed with enqueueIn.

export type EnqueueRef = mixed;

type EnqueueImplementation = {
  enqueue: (func: () => any, ms?: number) => EnqueueRef,
  cancel: (ref: any) => void,
};

let implementation: EnqueueImplementation = {
  enqueue: (func, ms = 0) => setTimeout(func, ms),
  cancel: (ref) => clearTimeout(ref),
};

export default function enqueueIn(func: () => mixed, ms?: number): EnqueueRef {
  return implementation.enqueue(func, ms);
}

export function cancelEnqueued(ref: any) {
  implementation.cancel(ref);
}

export function setImplementation(newImplementation: EnqueueImplementation) {
  implementation = newImplementation;
}



// WEBPACK FOOTER //
// ../nx-components/src/utils/enqueueIn.js